"use client";

import React, { useState, useEffect } from "react";
import { WageRate, EMPLOYEE_ROLE_TYPE, ROLE_TYPE_LABELS } from "../services/wage-rates-service";

export interface WageRateFormValues {
  employee_id?: number;
  branch_id?: number;
  role_type: number;
  rate_per_kg: number;
  effective_from: string;
  effective_to?: string;
  note?: string;
}

interface WageRatesModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: WageRateFormValues) => Promise<boolean>;
  isSubmitting?: boolean;
  editData?: WageRate | null;
  employees: Array<{ id: number; name: string }>;
  branches: Array<{ id: number; name: string }>;
}

const WageRatesModal: React.FC<WageRatesModalProps> = ({
  open,
  onClose,
  onSubmit,
  isSubmitting = false,
  editData,
  employees,
  branches,
}) => {
  const today = new Date().toISOString().split("T")[0];

  const [formValues, setFormValues] = useState<WageRateFormValues>({
    employee_id: undefined,
    branch_id: undefined,
    role_type: EMPLOYEE_ROLE_TYPE.PICKUP,
    rate_per_kg: 0,
    effective_from: today,
    effective_to: undefined,
    note: "",
  });

  const [errors, setErrors] = useState<Partial<Record<keyof WageRateFormValues, string>>>({});

  useEffect(() => {
    if (editData) {
      setFormValues({
        employee_id: editData.employee_id,
        branch_id: editData.branch_id,
        role_type: editData.role_type,
        rate_per_kg: editData.rate_per_kg,
        effective_from: editData.effective_from?.split("T")[0] || today,
        effective_to: editData.effective_to?.split("T")[0],
        note: editData.note || "",
      });
    } else {
      setFormValues({
        employee_id: undefined,
        branch_id: undefined,
        role_type: EMPLOYEE_ROLE_TYPE.PICKUP,
        rate_per_kg: 0,
        effective_from: today,
        effective_to: undefined,
        note: "",
      });
    }
    setErrors({});
  }, [editData, open, today]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof WageRateFormValues, string>> = {};

    if (formValues.rate_per_kg <= 0) {
      newErrors.rate_per_kg = "Đơn giá phải lớn hơn 0";
    }
    if (!formValues.effective_from) {
      newErrors.effective_from = "Vui lòng chọn ngày bắt đầu";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const success = await onSubmit(formValues);
    if (success) {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-[700px] max-w-[95vw] max-h-[90vh] overflow-y-auto p-6 shadow-2xl border border-slate-200">
        <div className="flex items-center justify-between border-b pb-3 mb-4">
          <h3 className="text-lg font-semibold text-slate-800">
            {editData ? "Cập nhật đơn giá" : "Thêm đơn giá mới"}
          </h3>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 text-lg"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Role Type */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Loại công việc <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              {[
                EMPLOYEE_ROLE_TYPE.PICKUP,
                EMPLOYEE_ROLE_TYPE.WASHER,
                EMPLOYEE_ROLE_TYPE.DELIVERY,
                EMPLOYEE_ROLE_TYPE.DRYER,
                EMPLOYEE_ROLE_TYPE.IRONER,
                EMPLOYEE_ROLE_TYPE.FOLDER,
              ].map((type) => (
                <label key={type} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="role_type"
                    value={type}
                    checked={formValues.role_type === type}
                    onChange={(e) => setFormValues({ ...formValues, role_type: Number(e.target.value) })}
                    disabled={!!editData}
                    className="w-4 h-4 text-green-600"
                  />
                  <span className="text-sm">{ROLE_TYPE_LABELS[type]}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Employee Select */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Nhân viên (để trống = áp dụng cho tất cả)
            </label>
            <select
              value={formValues.employee_id || 0}
              onChange={(e) => {
                const val = Number(e.target.value);
                setFormValues({ ...formValues, employee_id: val || undefined });
              }}
              disabled={!!editData}
              className={`w-full border rounded-lg px-3 py-2 text-sm border-slate-300 ${editData ? "bg-slate-100" : ""}`}
            >
              <option value={0}>Tất cả nhân viên</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.name}
                </option>
              ))}
            </select>
          </div>

          {/* Branch Select */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Chi nhánh (để trống = áp dụng cho tất cả)
            </label>
            <select
              value={formValues.branch_id || 0}
              onChange={(e) => {
                const val = Number(e.target.value);
                setFormValues({ ...formValues, branch_id: val || undefined });
              }}
              disabled={!!editData}
              className={`w-full border rounded-lg px-3 py-2 text-sm border-slate-300 ${editData ? "bg-slate-100" : ""}`}
            >
              <option value={0}>Tất cả chi nhánh</option>
              {branches.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>

          {/* Rate per kg */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Đơn giá (VND/kg) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              value={formValues.rate_per_kg}
              onChange={(e) => setFormValues({ ...formValues, rate_per_kg: Number(e.target.value) })}
              className={`w-full border rounded-lg px-3 py-2 text-sm ${
                errors.rate_per_kg ? "border-red-500" : "border-slate-300"
              }`}
              placeholder="VD: 5000"
              min={0}
            />
            {errors.rate_per_kg && (
              <p className="text-red-500 text-xs mt-1">{errors.rate_per_kg}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Effective From */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Hiệu lực từ ngày <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={formValues.effective_from}
                onChange={(e) => setFormValues({ ...formValues, effective_from: e.target.value })}
                disabled={!!editData}
                className={`w-full border rounded-lg px-3 py-2 text-sm ${
                  errors.effective_from ? "border-red-500" : "border-slate-300"
                } ${editData ? "bg-slate-100" : ""}`}
              />
              {errors.effective_from && (
                <p className="text-red-500 text-xs mt-1">{errors.effective_from}</p>
              )}
            </div>

            {/* Effective To */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Đến ngày (tùy chọn)
              </label>
              <input
                type="date"
                value={formValues.effective_to || ""}
                onChange={(e) => setFormValues({ ...formValues, effective_to: e.target.value || undefined })}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Ghi chú
            </label>
            <textarea
              value={formValues.note}
              onChange={(e) => setFormValues({ ...formValues, note: e.target.value })}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              rows={2}
              placeholder="Ghi chú về đơn giá..."
            />
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-100 transition"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
            >
              {isSubmitting ? "Đang xử lý..." : editData ? "Cập nhật" : "Thêm mới"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WageRatesModal;
